/**
 * Same-origin `/api/...` paths — keep in sync with `server/api.py` (@bp routes).
 * Use with `apiUrl()` from `./apiBase` unless the call site must be absolute.
 */
export const API_PREFIX = "/api" as const;

const enc = encodeURIComponent;

export function apiHealth(): string {
  return `${API_PREFIX}/health`;
}

export function apiTaskResult(taskId: string): string {
  return `${API_PREFIX}/result/${enc(taskId)}`;
}

export function apiAtomByQuantumNumber(data: string): string {
  return `${API_PREFIX}/atom-by-quantum-number/${enc(data)}`;
}

export function apiAtomShells(symbol: string): string {
  return `${API_PREFIX}/atom-shells/${enc(symbol.trim())}`;
}

export function apiMoleculeMoManifest(formula: string): string {
  return `${API_PREFIX}/molecule-mo-manifest/${enc(formula.trim())}`;
}

export function apiMoleculeCatalog(): string {
  return `${API_PREFIX}/molecule-catalog`;
}

/** Path only; pair with axios `params` for symbol, n, l, m, async. */
export function apiAtomicOrbital(): string {
  return `${API_PREFIX}/atomic-orbital`;
}

export function apiAtom(name: string): string {
  return `${API_PREFIX}/atom/${enc(name)}`;
}

export function apiMolecule(name: string): string {
  return `${API_PREFIX}/molecule/${enc(name)}`;
}

export function apiMoleculePub(name: string): string {
  return `${API_PREFIX}/moleculePUB/${enc(name)}`;
}

/** GPAW density JSON. Optional query params (handled server-side): `smiles`, `pubchem`. */
export function apiLoad(name: string): string {
  return `${API_PREFIX}/load/${enc(name)}`;
}

export function apiLoadPub(name: string): string {
  return `${API_PREFIX}/loadPUB/${enc(name)}`;
}

export function apiLoadSph(name: string): string {
  return `${API_PREFIX}/loadSPH/${enc(name)}`;
}

export function apiGetArticles(): string {
  return `${API_PREFIX}/get-articles`;
}

export function apiUpload(): string {
  return `${API_PREFIX}/upload`;
}

export function apiOpenbabel(): string {
  return `${API_PREFIX}/openbabel`;
}

export function apiDownloadGlb(key: string): string {
  return `${API_PREFIX}/downloadGLB/${enc(key)}`;
}

export function apiDownloadPng(key: string): string {
  return `${API_PREFIX}/downloadPNG/${enc(key)}`;
}

export function apiDownloadStl(key: string): string {
  return `${API_PREFIX}/downloadSTL/${enc(key)}`;
}

export function apiGetChemistryData(term: string): string {
  const q = new URLSearchParams({ term });
  return `${API_PREFIX}/get_chemistry_data?${q.toString()}`;
}

export function apiRetrieveSdf(cid: number | string): string {
  return `${API_PREFIX}/retrieve_sdf/${encodeURIComponent(String(cid))}`;
}

export function apiSmilesToIupac(smilesUrlEncoded: string): string {
  return `${API_PREFIX}/smiles_to_iupac/${smilesUrlEncoded}`;
}

export function apiGetChemistryImage(): string {
  return `${API_PREFIX}/get_chemistry_image`;
}

export function apiButtonClickLogged(): string {
  return `${API_PREFIX}/button_click_logged`;
}

export function apiEnergyDiagram(formula: string): string {
  return `${API_PREFIX}/energy-diagram/${enc(formula)}`;
}

export function apiMolecularOrbital(formula: string, orbital: string): string {
  const q = new URLSearchParams({ formula, orbital });
  return `${API_PREFIX}/molecular-orbital?${q.toString()}`;
}

export function apiQueueStatus(): string {
  return `${API_PREFIX}/queue-status`;
}

export function apiCredits(uid?: string, realm?: "mobile" | "web"): string {
  const params = new URLSearchParams();
  if (uid) params.set("uid", uid);
  if (realm) params.set("realm", realm);
  const qs = params.toString();
  return qs ? `${API_PREFIX}/credits?${qs}` : `${API_PREFIX}/credits`;
}

export function apiArticles(params?: { page?: number; limit?: number; q?: string; category?: string; featured?: boolean }): string {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.q) q.set("q", params.q);
  if (params?.category) q.set("category", params.category);
  if (params?.featured) q.set("featured", "true");
  const qs = q.toString();
  return `${API_PREFIX}/articles${qs ? `?${qs}` : ""}`;
}

/** POST — pull the newest arXiv papers into the articles collection. */
export function apiArticlesRefresh(): string {
  return `${API_PREFIX}/articles/refresh`;
}

export function apiArticleById(id: string): string {
  return `${API_PREFIX}/articles/${enc(id)}`;
}

export function apiProtein(pdbId: string, mode: string = "vdw"): string {
  return `${API_PREFIX}/protein/${enc(pdbId)}?mode=${enc(mode)}`;
}

export function apiComputeDensity(): string {
  return `${API_PREFIX}/compute-density`;
}

export function apiJobsSearch(query: string): string {
  return `${API_PREFIX}/jobs/search?q=${enc(query)}`;
}

export function apiJobDetail(taskId: string): string {
  return `${API_PREFIX}/jobs/${enc(taskId)}`;
}

/** Client auth helper; server route may be provided elsewhere. */
export function apiAuthRefresh(): string {
  return `${API_PREFIX}/refresh`;
}

export function apiRemoteNewSession(): string {
  return `${API_PREFIX}/remote/new-session`;
}

export function apiAuthDeviceLog(): string {
  return `${API_PREFIX}/auth/device-log`;
}

export function apiAuthDeviceInfo(uid?: string, email?: string): string {
  const q = new URLSearchParams();
  if (uid) q.set("uid", uid);
  if (email) q.set("email", email);
  return `${API_PREFIX}/auth/device-info?${q.toString()}`;
}

export function apiAuthDeleteAccount(): string {
  return `${API_PREFIX}/auth/delete-account`;
}

// ── Molecular workbench (server/workbench_api.py) ──────────────────────
export function apiWorkbenchHealth(): string {
  return `${API_PREFIX}/workbench/health`;
}

export function apiWorkbenchParse(): string {
  return `${API_PREFIX}/workbench/parse`;
}

export function apiWorkbenchExport(): string {
  return `${API_PREFIX}/workbench/export`;
}

export function apiWorkbenchHydrogens(): string {
  return `${API_PREFIX}/workbench/hydrogens`;
}

export function apiWorkbenchEmbed(): string {
  return `${API_PREFIX}/workbench/embed`;
}

export function apiWorkbenchOptimize(): string {
  return `${API_PREFIX}/workbench/optimize`;
}

export function apiWorkbenchEnergy(): string {
  return `${API_PREFIX}/workbench/energy`;
}

export function apiWorkbenchProperties(): string {
  return `${API_PREFIX}/workbench/properties`;
}

export function apiWorkbenchConformers(): string {
  return `${API_PREFIX}/workbench/conformers`;
}

/** Credit-gated GPAW hand-off (same gate as /api/load). */
export function apiWorkbenchDensity(): string {
  return `${API_PREFIX}/workbench/density`;
}
